import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Phone,
  Search,
  Plus,
  CheckCircle2,
  PauseCircle,
  Clock,
  Building2,
  ArrowRight,
  Loader2,
  BarChart3,
  Calendar,
  Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  active: { label: 'Active', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10' },
  onboarding: { label: 'Onboarding', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  paused: { label: 'Paused', icon: PauseCircle, color: 'text-slate-400', bg: 'bg-slate-500/10' }
};

export default function AdminClients() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers`);
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.owner_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: customers.length,
    active: customers.filter(c => c.status === 'active').length,
    onboarding: customers.filter(c => c.status === 'onboarding').length,
    totalLeads: customers.reduce((sum, c) => sum + (c.leads_captured || 0), 0)
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div 
                onClick={() => navigate('/')}
                className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center cursor-pointer"
              >
                <Phone className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">Alaska AI Services</span>
              <span className="text-slate-500 ml-2">/ Client Management</span>
            </div>
            <Button 
              onClick={() => navigate('/onboard')}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="new-client-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Client
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-sky-400" />
              <span className="text-slate-400 text-sm">Total Clients</span>
            </div>
            <div className="text-3xl font-bold">{stats.total}</div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-slate-400 text-sm">Active</span>
            </div>
            <div className="text-3xl font-bold text-green-400">{stats.active}</div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              <span className="text-slate-400 text-sm">Onboarding</span>
            </div>
            <div className="text-3xl font-bold text-yellow-400">{stats.onboarding}</div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              <span className="text-slate-400 text-sm">Total Leads</span>
            </div>
            <div className="text-3xl font-bold text-purple-400">{stats.totalLeads}</div>
          </motion.div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800/50 border-slate-700 text-white"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'active', 'onboarding', 'paused'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-sky-500 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
                data-testid={`filter-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Client List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">
              {searchQuery || statusFilter !== 'all' ? 'No clients found' : 'No clients yet'}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Add your first client to get started'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <Button onClick={() => navigate('/onboard')} className="bg-sky-500 hover:bg-sky-600">
                <Plus className="w-4 h-4 mr-2" />
                Add First Client
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCustomers.map((customer, index) => {
              const status = statusConfig[customer.status] || statusConfig.onboarding;
              const StatusIcon = status.icon;
              
              return (
                <motion.div
                  key={customer.customer_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/admin/clients/${customer.customer_id}`)}
                  className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 hover:border-sky-500/50 cursor-pointer transition-all group"
                  data-testid={`client-row-${customer.customer_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-slate-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg group-hover:text-sky-400 transition-colors">
                          {customer.business_name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <span>{customer.business_type_name || customer.business_type}</span>
                          <span>•</span>
                          <span>{customer.owner_name}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="hidden sm:flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <div className="text-slate-500">Leads</div>
                          <div className="font-semibold">{customer.leads_captured || 0}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-slate-500">Booked</div>
                          <div className="font-semibold">{customer.appointments_booked || 0}</div>
                        </div>
                      </div>
                      
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${status.bg}`}>
                        <StatusIcon className={`w-4 h-4 ${status.color}`} />
                        <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                      </div>
                      
                      <ArrowRight className="w-5 h-5 text-slate-600 group-hover:text-sky-400 transition-colors" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
